import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  GalleryItemDto,
  GalleryItemUpdateRequest,
  GalleryListQuery,
} from '@crm/shared';
import { api } from '../../lib/api';

const BASE = '/gallery';

export const galleryKeys = {
  all: ['gallery'] as const,
  list: (q: GalleryListQuery) => ['gallery', 'list', q] as const,
};

export function useGalleryList(filter: GalleryListQuery) {
  return useQuery({
    queryKey: galleryKeys.list(filter),
    queryFn: async (): Promise<GalleryItemDto[]> => {
      const params: Record<string, number> = {};
      if (filter.userId !== undefined) params.userId = filter.userId;
      if (filter.serviceId !== undefined) params.serviceId = filter.serviceId;
      const res = await api.get<GalleryItemDto[]>(BASE, { params });
      return res.data;
    },
  });
}

export type UploadGalleryInput = {
  /** Pracownik docelowy. Pracownik podaje własne ID; admin może podać cudze. */
  userId: number;
  file: File;
  caption?: string;
  serviceId?: number | null;
};

export function useGalleryUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadGalleryInput): Promise<GalleryItemDto> => {
      const form = new FormData();
      form.append('image', input.file);
      form.append('userId', String(input.userId));
      if (input.caption !== undefined && input.caption !== null) {
        form.append('caption', input.caption);
      }
      if (input.serviceId !== undefined && input.serviceId !== null) {
        form.append('serviceId', String(input.serviceId));
      }
      const res = await api.post<GalleryItemDto>(BASE, form, {
        // axios sam ustawi multipart/form-data z poprawnym boundary,
        // ale baseURL ma domyślnie 'application/json' jako Content-Type —
        // wymuszamy żeby axios go wykrył.
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: galleryKeys.all }),
  });
}

export function useGalleryUpdate(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: GalleryItemUpdateRequest): Promise<GalleryItemDto> => {
      const res = await api.patch<GalleryItemDto>(`${BASE}/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: galleryKeys.all }),
  });
}

export function useGalleryDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await api.delete(`${BASE}/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: galleryKeys.all }),
  });
}
